import React, { FC, ReactNode, useCallback, useEffect, useState, useMemo, ChangeEvent, FormEvent } from "react"
import {
    Button,
    Dialog,
    FormattedMessage,
    Grid,
    GridRow,
    Icon,
    Input,
    InputProps,
    PageTitle,
    useIntl,
    DialogDescription,
    SelectOption,
    Select,
    SelectProps,
} from "@metsooutotec/modes-react-components"
import { validateDecimal, validateSelect, validateText } from "../../../../validators/inputValidators"
import { StoreResults } from "../../../../stores/stores-helper"
import linerMaterialStore from "features/provisioning/liner-material-library/stores/liner-material-store"
import { NavigationCloseEntityOptions, resetRecordFormData } from "components/utils/modal-helper"
import styles from "./liner-material-library-modal.module.css"
import { LinerSpecification } from "../models/liner-material-library-models"

type LinerMaterialLibraryFields = Record<string, string>

interface LinerMaterialLibraryModalProps {
    isOpen: boolean | undefined
    onClose: (navigationCloseEntityOptions?: NavigationCloseEntityOptions) => void
    linerMaterial?: LinerSpecification | null
}

interface LinerMaterialLibraryFormFieldString {
    type: "string"
}

interface LinerMaterialLibraryFormFieldSelect {
    type: "select"
    options: SelectOption[]
}

interface LinerMaterialLibraryFormFieldBase {
    id: string
    label: string
    placeholder: string
    validate?: (value: string) => string
    inputMode?: string
    pattern?: string
    onFileUpload?: () => void
    unSelectFile?: () => void
    isRequired: boolean
    readOnly?: boolean
}

type LinerMaterialLibraryFormField = LinerMaterialLibraryFormFieldBase & (LinerMaterialLibraryFormFieldString | LinerMaterialLibraryFormFieldSelect)

const LinerMaterialLibraryModal: FC<LinerMaterialLibraryModalProps> = ({ isOpen, onClose, linerMaterial }) => {
    const { formatMessage } = useIntl()
    const { materialTypes } = linerMaterialStore
    const [isLoading, setLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<{ isError: boolean; errorMessage: string }>({ isError: false, errorMessage: "" })
    const [isEditing, setIsEditing] = useState<boolean>(Boolean(linerMaterial?.id))
    const [isFormModified, setIsFormModified] = useState<boolean>(false)

    // The following could be WearSense generalized.
    // Converter from BaseEntity (id, name, description) to SelectOption (text, value)
    const materialTypesOptions = useMemo(() => {
        return materialTypes.map(m => {
            return { text: m, value: m } as SelectOption
        })
    }, [materialTypes])

    const [formData, setFormData] = useState<LinerMaterialLibraryFields>({
        brand: linerMaterial?.brand || "",
        description: linerMaterial?.description || "",
        materialReference: linerMaterial?.materialReference || "",
        thickness: linerMaterial?.thickness?.toString() || "0",
        wearSurfaceThickness: linerMaterial?.wearSurfaceThickness?.toString() || "0",
        backingThickness: linerMaterial?.backingThickness?.toString() || "0",
        grade: linerMaterial?.grade || "",
    })

    const [formErrors, setFormErrors] = useState<Record<string, string>>({
        brand: "",
        materialType: "",
        thickness: "",
        wearSurfaceThickness: "",
        backingThickness: "",
    })

    const modalTitle = isEditing ? formatMessage("linerMaterialModal.editTitle") : formatMessage("linerMaterialModal.createTitle")

    useEffect(() => {
        if (linerMaterial) {
            const isSameSite = linerMaterial.id === formData.id
            if (isSameSite) return

            setIsEditing(Boolean(linerMaterial.id))
            setFormData({
                brand: linerMaterial?.brand || "",
                description: linerMaterial?.description || "",
                materialType: linerMaterial?.materialType || "",
                materialReference: linerMaterial?.materialReference || "",
                thickness: linerMaterial?.thickness?.toString() || "0",
                wearSurfaceThickness: linerMaterial?.wearSurfaceThickness?.toString() || "0",
                backingThickness: linerMaterial?.backingThickness?.toString() || "0",
                grade: linerMaterial?.grade || "",
            })
        } else {
            setIsEditing(false)
            resetRecordFormData(setFormData)
        }

        // Reset the form modification state when the asset changes
        setIsFormModified(false)
    }, [linerMaterial, formData.id])

    useEffect(() => {
        const newThickness = (Number(formData.wearSurfaceThickness) || 0) + (Number(formData.backingThickness) || 0)
        setFormData(prevData => ({
            ...prevData,
            thickness: newThickness.toString(),
        }))
    }, [formData.wearSurfaceThickness, formData.backingThickness])

    useEffect(() => {
        const { wearSurfaceThickness, backingThickness, materialType, grade, brand } = formData

        // Check if all values are empty
        const allFieldsEmpty = !wearSurfaceThickness && !backingThickness && !materialType && !grade && !brand

        if (!allFieldsEmpty) {
            let newMaterialReference = `${wearSurfaceThickness || ""}/${backingThickness || ""}/${materialType || ""}/${brand || ""}`
            if (grade) newMaterialReference += `/${grade}`

            setFormData(prevData => ({
                ...prevData,
                materialReference: newMaterialReference,
            }))
        } else {
            setFormData(prevData => ({
                ...prevData,
                materialReference: "",
            }))
        }
    }, [formData.wearSurfaceThickness, formData.backingThickness, formData.materialType, formData.grade, formData.brand])

    const validateForm = (formData: LinerMaterialLibraryFields): LinerMaterialLibraryFields => {
        const errors: LinerMaterialLibraryFields = {
            brand: validateText(formData.brand),
            materialType: validateSelect(formData.materialType),
            thickness: validateDecimal(formData.thickness),
            wearSurfaceThickness: validateDecimal(formData.wearSurfaceThickness),
            backingThickness: validateDecimal(formData.backingThickness),
        }
        return errors
    }

    const handleValidation = useCallback((fieldName: string, validationError: string | any): void => {
        setFormErrors(prevErrors => ({
            ...prevErrors,
            [fieldName]: validationError,
        }))
    }, [])

    const resetForm = (): void => {
        setIsEditing(false)
        resetRecordFormData(setFormData)
        resetRecordFormData(setFormErrors)
    }

    const handleChange = useCallback((fieldName: string, value: string) => {
        isError.isError = false
        setFormData(prevData => ({
            ...prevData,
            [fieldName]: value,
        }))
        setIsFormModified(true)
    }, [])

    const onCloseProcess = (): void => {
        resetForm()
        setIsError({ isError: false, errorMessage: "" })
        onClose({ passed: false })
    }

    const onClearClick = (inputId: string) => {
        setFormData(prevData => ({
            ...prevData,
            [inputId]: "",
        }))
        setFormErrors(prevErrors => ({
            ...prevErrors,
            [inputId]: "",
        }))
    }

    const handleSubmit = async (e: FormEvent): Promise<void> => {
        e.preventDefault()
        try {
            setLoading(true)

            // Check if the form has been modified
            if (isEditing && !isFormModified) {
                setIsError({
                    isError: true,
                    errorMessage: formatMessage("linerMaterialModal.error.modified"),
                })
                setLoading(false)
                return
            }
            // Validate the form
            const errors: Record<string, string> = validateForm(formData)
            const hasErrors = Object.values(errors).some(error => error !== "")

            if (hasErrors) {
                setIsError({ isError: true, errorMessage: formatMessage("linerMaterialModel.error.validation") })
                setFormErrors(errors)
                setLoading(false)
                return
            }

            const { backingThickness, materialType, materialReference, brand, thickness, wearSurfaceThickness, description, grade } = formData

            const linerMaterialDto: LinerSpecification = {
                backingThickness: Number(backingThickness),
                materialType,
                materialReference,
                brand,
                thickness: Number(thickness),
                wearSurfaceThickness: Number(wearSurfaceThickness),
                description,
                id: linerMaterial?.id || "",
                grade,
            }
            const requestResult: StoreResults = await (isEditing
                ? linerMaterialStore.editEntity(linerMaterialDto)
                : linerMaterialStore.createEntity(linerMaterialDto))

            if (!requestResult.passed) {
                setIsError({ isError: true, errorMessage: requestResult.message })
                setLoading(false)
                return
            }

            //Handle success
            console.log("Form submitted successfully!", requestResult.message, requestResult.entityId)
            resetForm() // Reset form data and errors
            onClose({ passed: true, entityName: materialReference })
        } catch (error) {
            setIsError({ isError: true, errorMessage: "Form submission error, please read logs" })
            console.error("Form submission error:", error)
        } finally {
            setLoading(false)
        }
    }

    const labels = {
        brand: formatMessage("linerMaterials.brand"),
        description: formatMessage("linerMaterials.description"),
        materialType: formatMessage("linerMaterials.materialType"),
        materialReference: formatMessage("linerMaterials.materialReference"),
        thickness: formatMessage("linerMaterials.thickness"),
        wearSurfaceThickness: formatMessage("linerMaterials.wearSurfaceThickness"),
        backingThickness: formatMessage("linerMaterials.backingThickness"),
        grade: formatMessage("linerMaterials.grade"),
    }

    const inputIdent: LinerMaterialLibraryFormField[] = [
        {
            id: "materialReference",
            label: labels.materialReference,
            placeholder: `Type ${labels.materialReference} here...`,
            type: "string",
            validate: undefined,
            isRequired: false,
            readOnly: true,
        },
        { id: "brand", label: labels.brand, placeholder: `Type ${labels.brand} here...`, type: "string", validate: validateText, isRequired: true },
        { id: "grade", label: labels.grade, placeholder: `Type ${labels.grade} here...`, type: "string", validate: undefined, isRequired: false },
        {
            id: "materialType",
            label: labels.materialType,
            placeholder: `Type ${labels.materialType} here...`,
            type: "select",
            options: materialTypesOptions,
            validate: validateSelect,
            isRequired: true,
        },
        {
            id: "description",
            label: labels.description,
            placeholder: `Type ${labels.description} here...`,
            type: "string",
            validate: undefined,
            isRequired: false,
        },
    ]

    const inputTechDetails: LinerMaterialLibraryFormField[] = [
        {
            id: "thickness",
            label: labels.thickness,
            placeholder: `${labels.thickness}`,
            type: "string",
            validate: validateDecimal,
            isRequired: false,
            readOnly: true,
        },
        {
            id: "wearSurfaceThickness",
            label: labels.wearSurfaceThickness,
            placeholder: `Type ${labels.wearSurfaceThickness} here...`,
            type: "string",
            validate: validateDecimal,
            isRequired: true,
        },
        {
            id: "backingThickness",
            label: labels.backingThickness,
            placeholder: `Type ${labels.backingThickness} here...`,
            type: "string",
            validate: validateDecimal,
            isRequired: true,
        },
    ]

    const renderInputs = (inputs: LinerMaterialLibraryFormField[]): ReactNode => {
        return (
            <>
                {inputs.map((input, index) => {
                    const { id, label, validate, type, placeholder, isRequired, readOnly } = input
                    const clearIcon = formData[id] && <Icon className='icon-cross-circle-fill-16' onClick={() => onClearClick(id)} />

                    switch (type) {
                        case "string": {
                            const inputProps: InputProps = {
                                id,
                                label,
                                placeholder,
                                validate,
                                onValidation: (validationError: string | undefined) => handleValidation(id, validationError),
                                onChange: (e: ChangeEvent<HTMLInputElement>) => handleChange(id, e.target.value),
                                type,
                                error: formErrors[id],
                                heightClass: "small",
                                spanning: true,
                                required: isRequired,
                                uiRequired: isRequired,
                                icon: clearIcon,
                                value: formData[id],
                                readOnly,
                            }

                            return (
                                <span className={styles.inputStyle}>
                                    <Input {...inputProps} />
                                </span>
                            )
                        }
                        case "select": {
                            const inputProps: SelectProps = {
                                id,
                                label,
                                placeholder,
                                validate,
                                onValidation: (validationError: string | undefined) => handleValidation(id, validationError),
                                onChange: (value: string | string[]) => handleChange(id, Array.isArray(value) ? value[0] : value),
                                type,
                                error: formErrors[id],
                                heightClass: "small",
                                spanning: true,
                                required: isRequired,
                                uiRequired: isRequired,
                                icon: clearIcon,
                                value: formData[id],
                                options: input.options,
                                freeText: false,
                                multiSelect: false,
                            }

                            return (
                                <span className={styles.inputStyle}>
                                    <Select {...inputProps} />
                                </span>
                            )
                        }
                        default:
                            console.warn("Unknown")
                            return null
                    }
                })}
            </>
        )
    }

    const renderInputIdent = (): ReactNode => {
        return <GridRow className={styles.gridRowStyle}>{renderInputs(inputIdent)}</GridRow>
    }

    const renderInputTechDetails = (): ReactNode => {
        return (
            <>
                <GridRow className={styles.gridRowStyle}>{renderInputs(inputTechDetails.slice(0, 3))}</GridRow>
            </>
        )
    }

    const rightPageButtons: ReactNode = (
        <>
            <Button loading={isLoading} disabled={isLoading} icon='icon-cross-circle-16' labelPosition='right' onClick={onCloseProcess} secondary>
                {formatMessage("linerMaterialModal.close")}
            </Button>
            <Button loading={isLoading} disabled={isLoading} icon='icon-save-16' labelPosition='right' onClick={handleSubmit} primary>
                {formatMessage("linerMaterialModal.save")}
            </Button>
        </>
    )

    return (
        <Dialog dimmer open={isOpen} size={"large"}>
            {isError.isError && (
                <DialogDescription>
                    <p className={styles.dialogDescriptionStyle}>{isError.errorMessage}</p>
                </DialogDescription>
            )}
            <PageTitle title={modalTitle} extraItems={rightPageButtons} />
            <div className={styles.itemViewListContainerStyle}>
                <h4>
                    <FormattedMessage id='linerMaterialModal.linerMaterialId' />
                </h4>
                <Grid columns={3} className={styles.gridStyle}>
                    {renderInputIdent()}
                </Grid>
                <div className={styles.itemViewSeparatorStyle} />
                <h4>
                    <FormattedMessage id='linerMaterialModal.techDetails' />
                </h4>
                <Grid columns={3} className={styles.gridStyle}>
                    {renderInputTechDetails()}
                </Grid>
                <div className={styles.itemViewSeparatorStyle} />
            </div>
            <div className={styles.requirementsMessageBlockStyle}>
                <Icon className='icon-info-o-24' /> {formatMessage("linerMaterialModal.requirements")}
            </div>
        </Dialog>
    )
}

export default LinerMaterialLibraryModal
